import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { isUsedForBusiness } from '@acorn-portal/shared/src/utils';
import {
	TAXI_ERRORS,
	VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES,
} from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { COVER_LEVEL } from '@acorn-portal/shared/src/utils/modalText';
import getClosestMileage from '@acorn-portal/shared/src/utils/getClosestMileage';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import * as Yup from 'yup';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { updateChangeOfUsage } from '@acorn-portal/shared/src/store/renewalChanges.slice';
import useRateRenewal from '@acorn-portal/shared/src/hooks/useRateRenewal';
import useRenewalNavigation from '@acorn-portal/shared/src/hooks/useRenewalNavigation';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IChangeUsageOrMileageForm {
	vehicleUse: string;
	levelOfCover: string;
	annualMileage: string;
	businessMileage?: string;
	licencingAuthority?: string;
	operatingArea?: string;
}

const ChangeOfUsageTaxi: FC = () => {
	const [open, toggleModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const { goToRenewalHome } = useRenewalNavigation();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const lists = useAppSelector((redux) => redux.referenceData);
	const renewalState = useAppSelector((redux) => redux.renewal);
	const { rateRenewal, loadingRenewalQuote } = useRateRenewal();

	useEffect(() => {
		setLoading(loadingRenewalQuote);
	}, [loadingRenewalQuote]);

	const initialValues = {
		vehicleUse: renewalState.vehicleDetails.vehicleUse,
		levelOfCover: renewalState.vehicleDetails.coverDefinitionCode ?? '',
		annualMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			renewalState.vehicleDetails.annualMileage,
		),
		businessMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			renewalState.vehicleDetails.businessMileage,
		),
		licencingAuthority:
			renewalState.vehicleDetails.licencingAuthority ?? fullPolicy.vehicleDetails.licencingAuthority ?? '',
		operatingArea: renewalState.policyDetails.operatingArea ?? fullPolicy.coverDetails.operatingArea ?? '',
	};

	const taxiValidationSchema = Yup.object({
		vehicleUse: Yup.string().required(VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES.vehicleUse),
		licencingAuthority: Yup.string().required(TAXI_ERRORS.licensingAuthority),
		operatingArea: Yup.string().required(TAXI_ERRORS.operatingArea),
	});
	const isTaxi = fullPolicy.product === Product.TAXI;

	const onSubmit = async (
		values: IChangeUsageOrMileageForm,
		formikHelpers: FormikHelpers<IChangeUsageOrMileageForm>,
	) => {
		if (
			isUsedForBusiness(values.vehicleUse) &&
			(values.businessMileage === undefined || values.businessMileage === '0')
		) {
			formikHelpers.setFieldError('businessMileage', 'Please provide business mileage');
			formikHelpers.setFieldTouched('businessMileage', true, false);
			return;
		}
		setLoading(true);
		const renewalUpdated = {
			...renewalState,
			vehicleDetails: {
				...renewalState.vehicleDetails,
				annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
				businessMileage: values.businessMileage ? parseInt(values.businessMileage.toString(), 10) : 0,
				coverDefinitionCode: values.levelOfCover,
				vehicleUse: values.vehicleUse,
				licencingAuthority: values.licencingAuthority ?? undefined,
			},
			policyDetails: {
				...renewalState.policyDetails,
				operatingArea: values.operatingArea ?? '',
			},
		};
		try {
			setLoading(true);
			dispatch(updateChangeOfUsage(true));
			await rateRenewal({ renewalState: renewalUpdated, fullPolicy });
		} catch (error) {
			console.error(error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	const taxiUseOptionsFilter = (lists.vehicleUseOptions as IOptions[]).filter((x) => x.value !== '4');

	const renewalCoverStartDate = fullPolicy.renewalDetails?.renewalCoverDetails.startDate;

	return (
		<JourneyCardLayout pageTitle='Change of Usage' loading={loading}>
			<Formik<IChangeUsageOrMileageForm>
				key={JSON.stringify(renewalState.newRenewalTransactionId)}
				validationSchema={taxiValidationSchema}
				initialValues={initialValues}
				onSubmit={onSubmit}
				onReset={goToRenewalHome}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						<ToggleButtonFormField
							label='What will you be using the vehicle for?'
							name='vehicleUse'
							id='vehicleUse'
							value={formik.values.vehicleUse}
							onChange={(value) => {
								formik.setFieldValue('vehicleUse', value);
								if (!isUsedForBusiness(value)) {
									formik.setFieldValue('businessMileage', undefined);
								}
							}}
							options={taxiUseOptionsFilter}
							error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
							helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
							product={fullPolicy.product}
						/>

						{isTaxi && (
							<>
								<AutoCompleteFormField
									label='Please select your licensing authority'
									name='licencingAuthority'
									id='licencingAuthority'
									value={formik.values.licencingAuthority}
									onChange={(value) => formik.setFieldValue('licencingAuthority', value)}
									options={lists.licencingArea as IOptions[]}
									error={formik.touched.licencingAuthority && Boolean(formik.errors.licencingAuthority)}
									helperText={formik.touched.licencingAuthority && formik.errors.licencingAuthority}
								/>
								<AutoCompleteFormField
									label='Primary operating area'
									name='operatingArea'
									id='operatingArea'
									value={formik.values.operatingArea}
									onChange={(value) => formik.setFieldValue('operatingArea', value)}
									options={lists.licencingArea as IOptions[]}
									error={formik.touched.operatingArea && Boolean(formik.errors.operatingArea)}
									helperText={formik.touched.operatingArea && formik.errors.operatingArea}
								/>
							</>
						)}

						<SelectFormField
							label='What level of cover do you require?'
							name='levelOfCover'
							id='levelOfCover'
							value={formik.values.levelOfCover}
							onChange={(value) => formik.setFieldValue('levelOfCover', value)}
							options={lists.levelOfCoverTypes as IOptions[]}
							error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
							helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
							modal={
								renewalCoverStartDate
									? {
											title: COVER_LEVEL(renewalCoverStartDate, fullPolicy.product).header,
											description: COVER_LEVEL(renewalCoverStartDate, fullPolicy.product).text,
										}
									: undefined
							}
						/>

						<ActionButtons>
							<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
						<HelperModal
							open={open}
							onClose={() => {
								toggleModal(false);
							}}
							title='Discard The Changes?'
							description='Are you sure you wish to discard the changes?'
							closeText='No'
							submitText='Yes'
							onSubmit={goToRenewalHome}
						/>
					</Form>
				)}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeOfUsageTaxi;
